/**
 * Airtable-style multi-filter builder for the To Do panel.
 * Stackable rules ([field] [operator] [value]) with Match all / Match any,
 * persisted per-user in localStorage.
 */
import { useMemo } from "react";
import { Plus, X, SlidersHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { KIND_META } from "./SalesTodoRow";

const KIND_LABELS = { chase: "Offer chase", credit_approved: "Docs chase", topup: "Top-up", reloan: "Re-loan", task: "Task" };

export const FILTER_FIELDS = {
  handler:  { label: "Handler", ops: ["is", "is not", "is empty", "is not empty"] },
  kind:     { label: "Type", ops: ["is", "is not"] },
  status:   { label: "Deal status", ops: ["is", "is not"] },
  priority: { label: "Traffic light", ops: ["is", "is not"] },
  text:     { label: "Name / SF", ops: ["contains", "does not contain"] },
  pct_paid: { label: "% repaid", ops: ["\u2265", "\u2264"] },
  dpd:      { label: "Arrears (days)", ops: ["\u2265", "\u2264"] },
  snoozed:  { label: "Snoozed", ops: ["is"] },
};
const NO_VALUE_OPS = new Set(["is empty", "is not empty"]);

export function newFilter() {
  return { id: Date.now() + Math.random(), field: "handler", op: "is", value: "" };
}

export function applyTodoFilters(items, filters, conjunction) {
  const active = filters.filter(f => NO_VALUE_OPS.has(f.op) || String(f.value).trim() !== "");
  if (active.length === 0) return items;
  const test = (item, f) => {
    switch (f.field) {
      case "handler": {
        if (f.op === "is empty") return !item.assigned_to;
        if (f.op === "is not empty") return !!item.assigned_to;
        return f.op === "is" ? item.assigned_to === f.value : item.assigned_to !== f.value;
      }
      case "kind":
        return f.op === "is" ? item.kind === f.value : item.kind !== f.value;
      case "status": {
        const s = item.status || "";
        return f.op === "is" ? s === f.value : s !== f.value;
      }
      case "priority": {
        const p = item.priority || "";
        return f.op === "is" ? p === f.value : p !== f.value;
      }
      case "text": {
        const hay = `${item.title || ""} ${item.sf_number || ""} ${item.contact_name || ""}`.toLowerCase();
        const hit = hay.includes(String(f.value).toLowerCase());
        return f.op === "contains" ? hit : !hit;
      }
      case "pct_paid": {
        const n = Number(item.pct_paid);
        if (!Number.isFinite(n)) return false;
        return f.op === "\u2265" ? n >= Number(f.value) : n <= Number(f.value);
      }
      case "dpd": {
        const n = Number(item.dpd);
        if (!Number.isFinite(n)) return false;
        return f.op === "\u2265" ? n >= Number(f.value) : n <= Number(f.value);
      }
      case "snoozed":
        return (f.value === "yes") === !!item.snoozed;
      default:
        return true;
    }
  };
  return items.filter(item =>
    conjunction === "any" ? active.some(f => test(item, f)) : active.every(f => test(item, f)));
}

function ValueControl({ f, set, users, statusValues }) {
  if (NO_VALUE_OPS.has(f.op)) return <div className="flex-1" />;
  const sel = (options) => (
    <Select value={f.value || undefined} onValueChange={(v) => set({ value: v })}>
      <SelectTrigger className="h-7 flex-1 text-[11px]" data-testid={`scal-filter-value-${f.id}`}>
        <SelectValue placeholder="Pick…" />
      </SelectTrigger>
      <SelectContent className="max-h-64">{options}</SelectContent>
    </Select>
  );
  switch (f.field) {
    case "handler":
      return sel(users.map(u => <SelectItem key={u.email} value={u.email} className="text-xs">{u.name || u.email}</SelectItem>));
    case "kind":
      return sel(Object.entries(KIND_LABELS).map(([k, l]) => {
        const Icon = KIND_META[k]?.icon;
        return <SelectItem key={k} value={k} className="text-xs">{Icon && <Icon className="w-3 h-3 inline mr-1 -mt-0.5" />}{l}</SelectItem>;
      }));
    case "status":
      return sel(statusValues.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>));
    case "priority":
      return sel([
        <SelectItem key="red" value="red" className="text-xs">🔴 Red</SelectItem>,
        <SelectItem key="amber" value="amber" className="text-xs">🟡 Amber</SelectItem>,
        <SelectItem key="green" value="green" className="text-xs">🟢 Green</SelectItem>,
      ]);
    case "snoozed":
      return sel([
        <SelectItem key="yes" value="yes" className="text-xs">Yes</SelectItem>,
        <SelectItem key="no" value="no" className="text-xs">No</SelectItem>,
      ]);
    case "pct_paid":
    case "dpd":
      return <Input type="number" className="h-7 flex-1 text-[11px]" value={f.value}
        onChange={(e) => set({ value: e.target.value })} placeholder="0" data-testid={`scal-filter-value-${f.id}`} />;
    default:
      return <Input className="h-7 flex-1 text-[11px]" value={f.value}
        onChange={(e) => set({ value: e.target.value })} placeholder="Type to search…" data-testid={`scal-filter-value-${f.id}`} />;
  }
}

export function TodoFilterBar({ filters, setFilters, conjunction, setConjunction, users, statusValues }) {
  const update = (id, patch) => setFilters(fs => fs.map(f => {
    if (f.id !== id) return f;
    const next = { ...f, ...patch };
    if (patch.field) {
      next.op = FILTER_FIELDS[patch.field].ops[0];
      next.value = "";
    }
    return next;
  }));
  const remove = (id) => setFilters(fs => fs.filter(f => f.id !== id));

  return (
    <div className="border-b bg-slate-50/70 px-2 py-2 space-y-1.5" data-testid="scal-filter-builder">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
          <SlidersHorizontal className="w-3 h-3" /> Filters
        </span>
        {filters.length > 1 && (
          <Select value={conjunction} onValueChange={setConjunction}>
            <SelectTrigger className="h-6 w-[110px] text-[10px]" data-testid="scal-filter-conjunction"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">Match ALL</SelectItem>
              <SelectItem value="any" className="text-xs">Match ANY</SelectItem>
            </SelectContent>
          </Select>
        )}
        <div className="ml-auto flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-6 text-[10px]" onClick={() => setFilters(fs => [...fs, newFilter()])} data-testid="scal-filter-add">
            <Plus className="w-3 h-3 mr-0.5" /> Add filter
          </Button>
          {filters.length > 0 && (
            <Button variant="ghost" size="sm" className="h-6 text-[10px] text-rose-600" onClick={() => setFilters([])} data-testid="scal-filter-clear">
              <Trash2 className="w-3 h-3 mr-0.5" /> Clear
            </Button>
          )}
        </div>
      </div>
      {filters.length === 0 && (
        <div className="text-[11px] text-slate-400">No filters — click &ldquo;Add filter&rdquo; to narrow by handler, type, status, traffic light…</div>
      )}
      {filters.map((f, i) => (
        <div key={f.id} className="flex items-center gap-1" data-testid={`scal-filter-row-${i}`}>
          <span className="text-[10px] text-slate-400 w-9 text-right shrink-0">{i === 0 ? "Where" : conjunction === "any" ? "or" : "and"}</span>
          <Select value={f.field} onValueChange={(v) => update(f.id, { field: v })}>
            <SelectTrigger className="h-7 w-[118px] text-[11px] shrink-0" data-testid={`scal-filter-field-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(FILTER_FIELDS).map(([k, cfg]) => <SelectItem key={k} value={k} className="text-xs">{cfg.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.op} onValueChange={(v) => update(f.id, { op: v, ...(NO_VALUE_OPS.has(v) ? { value: "" } : {}) })}>
            <SelectTrigger className="h-7 w-[100px] text-[11px] shrink-0" data-testid={`scal-filter-op-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {FILTER_FIELDS[f.field].ops.map(op => <SelectItem key={op} value={op} className="text-xs">{op}</SelectItem>)}
            </SelectContent>
          </Select>
          <ValueControl f={f} set={(p) => update(f.id, p)} users={users} statusValues={statusValues} />
          <button type="button" className="text-slate-400 hover:text-rose-600 shrink-0 p-1" onClick={() => remove(f.id)} data-testid={`scal-filter-remove-${i}`}>
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}

export function useStatusValues(items) {
  return useMemo(() => {
    const s = new Set();
    for (const i of items) if (i.status) s.add(i.status);
    return [...s].sort();
  }, [items]);
}
